// Two segments, one number: how many contacts they share (3.3). Both sides
// are counted with the same expressions a campaign selects with, so what a
// marketer reads here is what two campaigns would really have in common.

import { useState } from "react";
import { segmentOverlap } from "@/lib/api";
import type { OverlapResult, Segment } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { Toolbar } from "@/components/emc/Toolbar";
import { ErrorState } from "@/components/emc/states";
import { count, shareOf } from "@/components/emc/format";
import { messageOf } from "@/pages/segments/data";

export function OverlapPanel({ segments }: { segments: Segment[] }) {
  const [a, setA] = useState<number | null>(null);
  const [b, setB] = useState<number | null>(null);
  const [result, setResult] = useState<OverlapResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function compare() {
    if (a === null || b === null) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await segmentOverlap(a, b));
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  const pick = (label: string, value: number | null, set: (value: number | null) => void) => (
    <NativeSelect
      aria-label={label}
      className="min-w-56"
      value={value ?? ""}
      onChange={(event) => set(event.target.value === "" ? null : Number(event.target.value))}
    >
      <option value="">{label}</option>
      {segments.map((segment) => (
        <option key={segment.id} value={segment.id}>
          {segment.name}
        </option>
      ))}
    </NativeSelect>
  );

  return (
    <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc">
      <div className="flex flex-col gap-0.5">
        <h2>Átfedés</h2>
        <p className="text-muted-foreground">
          Két szegmens közös kontaktjai, ugyanazzal a szűréssel, amivel egy kampány válogat. Statikus szegmens csak
          felépítve számolható.
        </p>
      </div>
      <Toolbar>
        {pick("Első szegmens", a, setA)}
        {pick("Második szegmens", b, setB)}
        <Button type="button" disabled={busy || a === null || b === null} onClick={() => void compare()}>
          Összevetés
        </Button>
      </Toolbar>
      {error !== null && <ErrorState message={error} />}
      {result !== null && (
        <StatGrid>
          <StatTile label="Első" value={count(result.a_count)} />
          <StatTile label="Második" value={count(result.b_count)} />
          <StatTile label="Közös" value={count(result.both)} comparison={shareOf(result.both, result.a_count, "az első")} />
        </StatGrid>
      )}
    </section>
  );
}
